import React, { useEffect, useState } from 'react';
import { Smartphone, X, Download, Apple, Share2 } from 'lucide-react';
import { toast } from 'sonner';
import useInstallPrompt from '../hooks/useInstallPrompt';

// localStorage keys
const DISMISSED_AT_KEY = 'gx_pwa_prompt_dismissed_at';
const COOLDOWN_DAYS = 14;
const SHOW_AFTER_MS = 4500; // 4.5s after page load

function isDismissedRecently() {
  try {
    const v = localStorage.getItem(DISMISSED_AT_KEY);
    if (!v) return false;
    const ageDays = (Date.now() - Number(v)) / (1000 * 60 * 60 * 24);
    return ageDays < COOLDOWN_DAYS;
  } catch {
    return false;
  }
}

export default function PWAInstallBanner() {
  const { canInstall, isStandalone, isIOS, install } = useInstallPrompt();
  const [open, setOpen] = useState(false);
  const [showIOSHelp, setShowIOSHelp] = useState(false);

  useEffect(() => {
    if (isStandalone) return undefined;            // already installed, never show
    if (isDismissedRecently()) return undefined;    // recently dismissed
    if (!canInstall && !isIOS) return undefined;    // browser can't install + not iOS → silent
    const t = setTimeout(() => setOpen(true), SHOW_AFTER_MS);
    return () => clearTimeout(t);
  }, [canInstall, isStandalone, isIOS]);

  const dismiss = () => {
    try { localStorage.setItem(DISMISSED_AT_KEY, String(Date.now())); } catch { /* noop */ }
    setOpen(false);
  };

  const handleInstall = async () => {
    if (isIOS) { setShowIOSHelp(true); return; }
    const res = await install();
    if (res?.outcome === 'accepted') {
      toast.success('Installed! Open Gynovate AI/SI from your home screen.');
      setOpen(false);
    } else if (res?.outcome === 'dismissed') {
      dismiss();
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[90] w-[calc(100%-2rem)] max-w-md animate-fade-in"
      role="dialog"
      aria-label="Install Gynovate AI/SI"
      data-testid="pwa-install-banner"
    >
      <div
        className="relative rounded-2xl overflow-hidden shadow-2xl border border-raspberry-100 backdrop-blur-xl"
        style={{ background: 'linear-gradient(140deg, rgba(255,255,255,0.92) 0%, rgba(255,245,246,0.92) 70%, rgba(255,252,248,0.92) 100%)' }}
      >
        {/* Decorative glow */}
        <div className="absolute -top-10 -right-10 w-32 h-32 rounded-full bg-raspberry-100 opacity-60 blur-2xl pointer-events-none" />

        {!showIOSHelp ? (
          <div className="relative p-4 sm:p-5 flex items-center gap-3">
            <div className="flex-shrink-0 w-11 h-11 rounded-xl bg-gradient-to-br from-raspberry-800 to-[#7a0e2e] flex items-center justify-center shadow-lg shadow-raspberry-200">
              <Smartphone className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-slate-900 leading-tight">Install Gynovate AI/SI for faster access</p>
              <p className="text-[11px] text-slate-600 mt-0.5 leading-snug">Stay signed in · Opens in its own window · Works offline</p>
            </div>
            <div className="flex items-center gap-1.5 flex-shrink-0">
              <button
                onClick={handleInstall}
                className="inline-flex items-center gap-1 px-3 py-2 rounded-xl bg-raspberry-800 hover:bg-raspberry-700 text-white text-xs font-bold transition-colors shadow-md"
                data-testid="pwa-banner-install-btn"
              >
                {isIOS ? <Apple className="w-3.5 h-3.5" /> : <Download className="w-3.5 h-3.5" />}
                Install
              </button>
              <button
                onClick={dismiss}
                className="p-2 rounded-xl text-slate-500 hover:text-slate-900 hover:bg-slate-100 transition-colors"
                aria-label="Dismiss"
                data-testid="pwa-banner-dismiss"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        ) : (
          <div className="relative p-5">
            <button
              onClick={dismiss}
              className="absolute top-3 right-3 p-1.5 rounded-lg text-slate-500 hover:text-slate-900 hover:bg-slate-100"
              aria-label="Dismiss"
            >
              <X className="w-4 h-4" />
            </button>
            <div className="flex items-center gap-2 text-raspberry-800 font-bold text-sm mb-2">
              <Apple className="w-4 h-4" /> Install on iPhone / iPad
            </div>
            <ol className="text-xs text-slate-700 leading-relaxed space-y-1.5 list-decimal pl-4">
              <li>Tap the <Share2 className="inline w-3.5 h-3.5 align-text-bottom mx-0.5" /> <b>Share</b> button at the bottom of Safari.</li>
              <li>Scroll down and tap <b>&ldquo;Add to Home Screen&rdquo;</b>.</li>
              <li>Tap <b>Add</b> — Gynovate AI/SI will appear on your home screen.</li>
            </ol>
            <button
              onClick={dismiss}
              className="mt-4 w-full px-4 py-2 rounded-xl bg-slate-900 text-white text-xs font-bold hover:bg-slate-800 transition-colors"
            >
              Got it
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
